<script setup lang="ts">
import { ref, useDefaults, useWindowScroll } from '#imports'
import type { UIPageNavigationProps } from '@core/types'

const _props = withDefaults(defineProps<UIPageNavigationProps>(), {
    mobile: undefined,
    navigation: undefined,
    navbarTop: undefined,
    fixedNavigation: undefined,
    searchEnabled: undefined,
    searchUrl: undefined,
    searchTitle: undefined,
})

const props = useDefaults({
    props: _props,
    defaults: {
        navigation: [],
        navbarTop: 60,
        fixedNavigation: true,
        searchEnabled: false,
        searchUrl: '/lobby#game-search',
        searchTitle: 'Find game',
    },
})

const emit = defineEmits(['close-user-notifications-drawer', 'activate-search'])

const { y: scrollY } = useWindowScroll(window)

const userMenuOpenCompat = ref(false)

function toggleUserMenuCompat() {
    userMenuOpenCompat.value = !userMenuOpenCompat.value
}

function closeUserMenuCompat() {
    userMenuOpenCompat.value = false
}
</script>

<template>
    <nav class="nav">
        <div class="nav__brand">
            <slot name="brand" />
        </div>

        <div class="nav__user-actions">
            <slot
                name="user-actions"
                :state="{ userMenuOpen: userMenuOpenCompat }"
                :methods="{ toggleUserMenu: toggleUserMenuCompat, closeUserMenu: closeUserMenuCompat }"
            />
        </div>

        <ul
            class="nav__primary"
            :class="{ 'nav__primary--fixed': scrollY >= (props.navbarTop ?? 0) && props.fixedNavigation }"
        >
            <slot>
                <li v-if="props.searchEnabled">
                    <NuxtLink
                        v-slot="{ navigate }"
                        :to="props.searchUrl"
                        :title="props.searchTitle"
                        class="nav__search-link"
                        custom
                    >
                        <a @click="navigate" @click.prevent="emit('activate-search')">
                            <UIIcon name="search" />
                        </a>
                    </NuxtLink>
                </li>
                <li v-for="route in props.navigation" :key="route.name">
                    <NuxtLink :href="route.href || route.path" :external="route.external">
                        {{ route.title }}
                    </NuxtLink>
                </li>
            </slot>
        </ul>
        <div class="nav__primary-fixed-spacer" />
    </nav>
</template>

<style scoped lang="scss">
%nav-link {
    white-space: nowrap;
    font-size: $font-size-sm;
    font-weight: $text-medium;
    align-items: center;
    color: $nav-font-color;
    text-shadow: none;
    text-decoration: none;

    @media (min-width: $screen-md) {
        text-shadow: 1px 1px 1px white;
    }

    @media (min-width: $screen-lg) {
        font-size: $font-size-lg;
    }

    @media (min-width: $screen-xl) {
        font-size: $font-size-xl;
    }

    @media (min-width: $screen-md) {
        &:hover,
        &:focus {
            color: $link-hover-color;
            text-decoration: none;
        }
    }

    &.nuxt-link-exact-active {
        color: $link-color;
    }
}

.nav {
    display: grid;
    position: relative;
    grid-template:
        'brand user-actions' $nav-user-actions-height
        'primary primary' 1fr / 1fr 1fr;
    user-select: none;
    align-items: center;
    z-index: z(site-nav);

    @media (min-width: $screen-md) {
        grid-template: 'brand primary user-actions' $nav-user-actions-height / auto auto auto;
    }

    @media (min-width: $screen-lg) {
        max-width: $container-lg;
        margin: 0 auto;
        grid-template:
            'brand primary user-actions' $nav-user-actions-height
            'checkbox . .' auto / 0.5fr auto 0.5fr;
    }

    @media (min-width: $screen-xl) {
        max-width: $container-xl;
    }

    a.router-link-exact-active {
        color: $link-color;
    }

    &__brand {
        display: flex;
        grid-area: brand;
        padding-left: $gutter-size;

        @media (min-width: $screen-md) {
            padding-left: $gutter-size * 2;
        }
    }

    &__user-actions {
        display: flex;
        grid-area: user-actions;
        justify-self: flex-end;
        gap: 5px;
        padding-right: 5px;

        @include mobile {
            gap: 10px;
            padding-right: 13px;
        }

        @media (min-width: $screen-md) {
            padding-right: $gutter-size * 2;
        }

        .btn--link:not(:hover) {
            color: $nav-font-color;
        }
    }

    &__primary {
        display: grid;
        grid-auto-flow: column;
        gap: 0;
        grid-area: primary;
        justify-items: center;
        margin: 0;
        padding: 5px 0;
        background-color: $nav-bg-color;
        max-width: 100vw;
        min-height: 34px;

        @media (min-width: $screen-md) {
            border-top: none;
            background-color: transparent;
        }

        a {
            @extend %nav-link;
            text-shadow: none;

            @media (min-width: $screen-md) {
                height: $nav-user-actions-height;
                font-weight: $text-medium;
                padding: 0 15px;
            }

            @media (min-width: $screen-xl) {
                position: relative;
            }
        }

        &--fixed {
            transition: box-shadow 0.3s ease-out 0.1s;

            @media (max-width: #{$screen-md - 1}) {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: z(site-nav);
                box-shadow: $nav-sticky-shadow;
            }

            & + .nav__primary-fixed-spacer {
                @media (max-width: #{$screen-md - 1}) {
                    overflow: hidden;
                    margin-top: 34px;
                }
            }
        }
    }

    &__user-balance {
        flex: 1;
        color: $nav-user-balance-color;
        font-size: $font-size-sm;
        font-weight: $text-medium;
        text-align: right;

        @media (min-width: $screen-md) {
            flex: none;
            padding-left: 30px; /* prevent sticking to menu item text */
            color: $nav-font-color;
            white-space: nowrap;
        }

        @media (min-width: $screen-lg) {
            font-size: $font-size-base;
        }
    }

    &__search-link {
        cursor: pointer;
    }
}

.simple-layout {
    .nav {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: auto;
        grid-template-areas: 'brand primary';

        &__primary {
            background-color: transparent;
            padding: 0;
        }
    }
}
</style>
